"use client";

import { useSyncExternalStore } from "react";
import type { ContentTooltipOpenOn } from "./content-tooltip-types";

function subscribeCoarsePointer(onStoreChange: () => void): () => void {
	if (typeof window === "undefined") {
		return () => {};
	}
	const mq = window.matchMedia("(hover: none) and (pointer: coarse)");
	mq.addEventListener("change", onStoreChange);
	return () => mq.removeEventListener("change", onStoreChange);
}

function getCoarsePointerSnapshot(): boolean {
	if (typeof window === "undefined") {
		return false;
	}
	return window.matchMedia("(hover: none) and (pointer: coarse)").matches;
}

/** True when primary input is coarse (touch) and hover is unavailable. */
export function usePrefersCoarsePointer(): boolean {
	return useSyncExternalStore(
		subscribeCoarsePointer,
		getCoarsePointerSnapshot,
		() => false,
	);
}

export interface ResolvedContentTooltipOpenOn {
	enableHover: boolean;
	enableClick: boolean;
	/** Coarse pointer prefers tap; used for delay / arm policy. */
	coarse: boolean;
}

/**
 * Resolve openOn against coarse pointer.
 * Default product intent: hover on fine pointers; tap on coarse; hoverAndTap enables both.
 */
export function resolveContentTooltipOpenOn(
	openOn: ContentTooltipOpenOn | undefined,
	coarse: boolean,
): ResolvedContentTooltipOpenOn {
	const mode = openOn ?? (coarse ? "tap" : "hover");
	switch (mode) {
		case "hover":
			return { enableHover: true, enableClick: false, coarse };
		case "tap":
			return { enableHover: false, enableClick: true, coarse };
		case "hoverAndTap":
			return { enableHover: true, enableClick: true, coarse };
		case "controlled":
			return { enableHover: false, enableClick: false, coarse };
	}
}

export function useResolvedContentTooltipOpenOn(
	openOn: ContentTooltipOpenOn | undefined,
): ResolvedContentTooltipOpenOn {
	const coarse = usePrefersCoarsePointer();
	return resolveContentTooltipOpenOn(openOn, coarse);
}
